import { GEO_RASTER_TYPES } from '@/components/features/assets/categorize'
import { detectCogType } from '@/lib/tile-utils'

import {
  AssetServiceClient,
  SLIM_MAX_PAGE_SIZE,
} from '@droidmap/asset-service-contract'
import { DroidMapMetadata } from '@droidmap/shared-service-contract'

/** A georeferenced raster the advanced map can toggle as a layer. */
export type RasterLayer = { id: string; name: string }

/**
 * Order raster layers so the orthophoto (the natural base image) is first —
 * the advanced map shows only the first layer by default, so putting the
 * ortho first gives the most useful initial view, with DSM/DTM available to
 * toggle on top.
 */
export const rasterOrderRank = (name: string): number => {
  switch (detectCogType(name)) {
    case 'dsm':
      return 2
    case 'dtm':
      return 3
    default:
      return 0 // orthophoto / unknown → base image, show first
  }
}

/**
 * Every georeferenced raster in a set, ordered for the layer panel.
 *
 * Asks the server for those types (droidmap#2887) rather than listing the set
 * and filtering here. Before the filter existed this walked every asset in
 * the set through the slim projection (#2774) — and for a drone-image set
 * that is thousands of images fetched to produce an empty layer list. Now the
 * worst case is one request that returns nothing.
 *
 * Lives here rather than inline in the route so it can be tested without
 * mounting MapLibre.
 */
export const collectRasterLayers = async (
  assetClient: AssetServiceClient,
  assetSetId: string,
  metadata: DroidMapMetadata,
): Promise<RasterLayer[]> => {
  const layers: RasterLayer[] = []
  let next: string | undefined = undefined
  do {
    const page = await assetClient.listSlimAssetsByAssetSet(
      {
        assetSetId,
        pageSize: SLIM_MAX_PAGE_SIZE,
        next,
        assetTypes: GEO_RASTER_TYPES,
      },
      metadata,
    )
    for (const asset of page.items ?? []) {
      layers.push({ id: asset.id, name: asset.name })
    }
    next = page.next
  } while (next)

  return layers.sort(
    (a, b) => rasterOrderRank(a.name) - rasterOrderRank(b.name),
  )
}
